import { Copiar } from "./Copiar";
import type { UseQueryResult } from "@tanstack/react-query";
import { formatUnits } from "viem";
import type { Vault } from "../hooks/useVaults";
import { useMultiVault } from "../hooks/useMultiVault";
import { useAperturas, type Apertura } from "../hooks/useApertura";
import type { RedKeel } from "../config/networks";
import { Banda } from "./Banda";
import { ParMonedas } from "./Monedas";
import { vistaPrecio } from "../lib/precio";
import { num, antiguedad } from "../lib/formato";
import { useCobrado, type Cobrado } from "../lib/ganado";
import { useT } from "../lib/i18n";

/**
 * Lo ganado por una piscina DESDE QUE ABRIÓ, en su moneda de referencia.
 *
 * DESDE QUE ABRIÓ, y no «lo que hay ahora apartado»: `withdrawFees()` pone `profit0/1` a cero, así
 * que al cobrar las ganancias esta cifra se desplomaba a casi nada y parecía que lo ganado se
 * borraba al retirarlo. Lo ganado es historia — cobrarlo no lo deshace.
 *
 * El histórico sale de los eventos (`useCobrado`, en `lib/ganado.ts`) y ya incluye lo que sigue
 * apartado en `profit0/1`, así que sumar los dos contaría dos veces lo mismo. A eso se le añade lo
 * que TODAVÍA está dentro del NFT, que aún no ha pasado por ningún evento.
 *
 * Va en BRUTO, igual que la tarjeta «Ganado» de la ficha: es la cifra que se puede cruzar con lo que
 * enseña Uniswap de la posición. El 10% se explica donde se cobra.
 *
 * Las comisiones se apartan en LOS DOS tokens, así que sumarlos a pelo sería sumar peras con
 * manzanas: el del activo se convierte a la referencia con el precio del pool.
 *
 * Devuelve `null` cuando el vault no sabe decirlo —los clones anteriores al 2026-08-09 no tienen
 * esos getters— en vez de un 0: «no lo sé» y «no has ganado nada» no se pueden pintar igual.
 */
function ganado(v: Vault, c?: Cobrado, capitalRaw?: bigint): { importe: number; pct: number | null; sinCobrar: number } | null {
  const sabe = v.profit0 !== undefined || v.profit1 !== undefined
    || v.pendiente0 !== undefined || v.pendiente1 !== undefined;
  if (!sabe) return null;
  if (v.d0 === undefined || v.d1 === undefined || v.precio == null) return null;

  // `precio` es token1 por token0: para pasar token0 a token1 se multiplica, y al revés se divide.
  const aRef = (a0: bigint | undefined, a1: bigint | undefined) => {
    const x0 = Number(formatUnits(a0 ?? 0n, v.d0!));
    const x1 = Number(formatUnits(a1 ?? 0n, v.d1!));
    return v.refEsToken0 ? x0 + (v.precio! > 0 ? x1 / v.precio! : 0) : x1 + x0 * v.precio!;
  };

  // DOS SITIOS, UNA CIFRA. Lo que la posición ya cobró alguna vez —esté apartado, retirado o
  // reinvertido— y lo que sigue DENTRO del NFT hasta que el robot reajuste o el dueño lo pida.
  // Enseñar solo lo apartado hacía dos cosas mal: que una piscina de dos días dijera «ganado 0»
  // estando en rango desde el minuto uno, y que retirar la ganancia pareciera borrarla.
  //
  // EL MAYOR DE LOS DOS, TOKEN A TOKEN, y no uno u otro. Los dos son cotas por debajo del total de
  // verdad, y cada uno falla por un lado:
  //
  //   · lo apartado (`profit`) se vacía al retirar — el fallo que arregló todo esto;
  //   · el histórico sale de la comisión cobrada (bruto = comisión × 10000/bps), y CUANDO LA
  //     COMISIÓN REDONDEA A CERO ese cobro no deja rastro. Visto en `0x02EDdE73` (WBTC/USDC): dos
  //     cobros con `fee0 = 0` y `fee1 = 815` y `556`, con 14 unidades de WBTC apartadas de verdad.
  //     En un token de 8 decimales, el 10% de una cantidad diminuta es 0 en enteros.
  //
  // Se compara por TOKEN porque el redondeo ocurre en uno y no en el otro: en ese vault gana el
  // histórico en USDC y lo apartado en WBTC.
  const mayor = (a: bigint | undefined, b: bigint | undefined) => ((a ?? 0n) > (b ?? 0n) ? a! : b ?? 0n);
  // MIENTRAS EL HISTÓRICO NO LLEGA se enseña lo apartado, que es lo único que se sabe sin eventos.
  // Los eventos van en su propia consulta —en BNB Chain tardan lo suyo— y la cifra se completa sola
  // en cuanto responden: más vale una cifra corta y honesta a la primera que una lista en blanco.
  const historico = c
    ? aRef(mayor(c.bruto0, v.profit0), mayor(c.bruto1, v.profit1))
    : aRef(v.profit0, v.profit1);
  const sinCobrar = aRef(v.pendiente0, v.pendiente1);
  const importe = historico + sinCobrar;

  // El capital para el % es el de APERTURA cuando supera al de estado en vivo: el vivo baja al
  // retirar principal (`costBasisRef -= salida`) y dividir el ganado de toda la vida por lo que
  // queda dispara el porcentaje. `capitalRaw` ya viene con `max(vivo, apertura)` desde la fila.
  const capital = Number(formatUnits(capitalRaw ?? v.costBasisRef, v.decimalesRef ?? 6));
  return { importe, pct: capital > 0 ? (importe / capital) * 100 : null, sinCobrar };
}


type Props = {
  consulta: UseQueryResult<Vault[], Error>;
  red: RedKeel;
  alAbrir: (d: Vault["direccion"]) => void;
};

/**
 * La lista de vaults, con los cuatro estados que una pantalla que lee de una cadena TIENE que
 * saber contar: cargando, error, vacio y con datos. En la app vieja el error se tragaba y el
 * usuario veia "Aun no tienes inversiones" cuando en realidad el RPC habia fallado — que es la
 * peor de las cuatro, porque parece buena noticia.
 */
export function ListaVaults({ consulta, red, alAbrir }: Props) {
  const t = useT();
  // Antes de los returns tempranos: los hooks no pueden ir detrás de un `if`.
  const multi = useMultiVault(red);

  if (consulta.isPending) {
    return (
      <div className="tarjetas">
        {[0, 1].map((i) => <div key={i} className="tarjeta esqueleto" aria-hidden />)}
      </div>
    );
  }

  if (consulta.isError) {
    return (
      <div className="aviso error">
        <p>{t("No pude leer tus vaults de {red}.", { red: red.etiqueta })}</p>
        <p className="detalle">{consulta.error.message}</p>
        <button className="btn" onClick={() => consulta.refetch()}>{t("Reintentar")}</button>
      </div>
    );
  }

  if (consulta.data.length === 0) {
    return (
      <div className="vacio">
        <p>{t("Aún no tienes ninguna piscina en {red}.", { red: red.etiqueta })}</p>
        <p className="detalle">{t("Elige una pool abajo y ábrela: tarda tres firmas.")}</p>
      </div>
    );
  }

  // ARCHIVADO, sin guardar nada en ninguna parte: un vault del que ya sacaste todo tiene la
  // posicion cerrada Y el capital aportado a cero, porque `withdraw()` pone `costBasisRef = 0`.
  // Un vault recien creado, en cambio, tiene el DEPOSITO dentro pero todavia no tokenId — y ese SI
  // tiene que seguir a la vista, o el usuario pierde la pantalla justo entre depositar y abrir.
  // OJO: `deposit()` NO toca `costBasisRef` mientras no haya posicion (solo `open()` lo hace), asi
  // que «depositado sin abrir» se reconoce por el SALDO dentro del vault, no por costBasisRef —que
  // es cero, igual que un vault vacio—. Sin esto, un vault con dinero esperando caia en «Cerradas».
  const pendienteAbrir = (v: Vault) =>
    v.tokenId === 0n && ((v.libre0 ?? 0n) > 0n || (v.libre1 ?? 0n) > 0n);
  const activos = consulta.data.filter((v) => v.tokenId !== 0n || v.costBasisRef !== 0n || pendienteAbrir(v));
  const archivados = consulta.data.filter(
    (v) => v.tokenId === 0n && v.costBasisRef === 0n && !pendienteAbrir(v)
  );

  return (
    <>
      {activos.length > 0 && <ResumenFlota vaults={activos} red={red} />}

      {activos.length > 0 ? (
        <ListaPiscinas vaults={activos} red={red} alAbrir={alAbrir} />
      ) : (
        <div className="vacio">
          <p>{t("No tienes ninguna piscina abierta en {red}.", { red: red.etiqueta })}</p>
          <p className="detalle">{t("Elige una pool abajo y ábrela: tarda tres firmas.")}</p>
        </div>
      )}

      {archivados.length > 0 && (
        <details className="bloque archivados">
          <summary>
            {t("Cerradas")} <span className="opcional">{archivados.length}</span>
          </summary>
          <div className="cuerpo">
            {/* El texto cambia con la generacion de la factory, y no es un matiz: decirle a alguien
                que su vault cerrado "se reutiliza" cuando en realidad se le va a crear otro nuevo
                le hace esperar la cuota y los ajustes que ya tenia puestos. */}
            <p className="detalle" style={{ margin: 0 }}>
              {t("Vacías: sacaste todo. El vault sigue en la cadena con su historial, y")}{" "}
              {multi.data ? (
                <>
                  {t("volver a abrir en ese par")} <strong>{t("crea una piscina nueva")}</strong>{t(", con su propio dinero y sus propios ajustes. Ésta se queda aquí como registro.")}
                </>
              ) : (
                <>
                  <strong>{t("se puede volver a abrir")}</strong> {t("en su mismo par — elígelo abajo como cualquier otra pool y se reutiliza, con su cuota y sus ajustes ya puestos.")}
                </>
              )}
            </p>
            <ListaPiscinas vaults={archivados} red={red} alAbrir={alAbrir} />
          </div>
        </details>
      )}
    </>
  );
}

/**
 * Las cuatro cifras de la flota, con la misma forma que las de la ficha de un vault.
 *
 * LA CUARTA ES LA QUE NO EXISTIA. Para enterarse de que a una piscina se le esta acabando el gas
 * —o de que quedo pausada, o sin permisos— habia que ENTRAR EN CADA UNA. Un vault sin gas deja de
 * reajustarse y no avisa desde ninguna parte: la lista es justo el sitio donde eso tiene que
 * saltar, porque es la pantalla que se abre para echar un ojo.
 *
 * Se calcula con lo que la lista YA lee (no añade ni una peticion): el capital de cada vault, si
 * esta en rango con su banda, si esta pausado y si su logica esta al dia.
 */
function ResumenFlota({ vaults, red }: { vaults: Vault[]; red: RedKeel }) {
  const t = useT();
  const dec = vaults[0]?.decimalesRef ?? 6;
  const ref = vaults[0]?.simboloRef ?? red.simboloEstable;

  // El dinero de cada vault: normalmente `costBasisRef`, pero un vault DEPOSITADO SIN ABRIR lo tiene
  // a cero (solo `open()` lo fija) con el depósito suelto dentro — ese se valora en la referencia con
  // el precio, o «Tu dinero» dejaría fuera justo el que hay que abrir.
  const dineroDe = (v: Vault) => {
    if (!(v.tokenId === 0n && v.costBasisRef === 0n))
      return Number(formatUnits(v.costBasisRef, v.decimalesRef ?? dec));
    const l0 = Number(formatUnits(v.libre0 ?? 0n, v.d0 ?? 18));
    const l1 = Number(formatUnits(v.libre1 ?? 0n, v.d1 ?? 18));
    const p = v.precio ?? 0; // token1 por token0
    return v.refEsToken0 ? l0 + (p > 0 ? l1 / p : 0) : l1 + l0 * p;
  };
  const capital = vaults.reduce((t, v) => t + dineroDe(v), 0);
  const abiertas = vaults.filter((v) => v.tokenId !== 0n).length;
  const enRango = vaults.filter(
    (v) => v.tokenId !== 0n && v.precio != null && v.lo != null && v.hi != null
      && v.precio >= v.lo && v.precio < v.hi
  ).length;

  // Lo que hace que una piscina deje de trabajar sin decirlo: pausada, o con dinero dentro y sin
  // posicion abierta. El gas y la cuota no llegan a esta consulta, y pedirlos aqui serian dos
  // lecturas mas por vault: se avisa de lo que se sabe, y la ficha cuenta el resto.
  const paradas = vaults.filter(
    (v) => v.pausado || (v.tokenId === 0n && ((v.libre0 ?? 0n) > 0n || (v.libre1 ?? 0n) > 0n))
  );

  return (
    <div className="cifras">
      <div className="cifra">
        <span className="k">{t("Tu dinero")}</span>
        <span className="v">{num(capital)} <small>{ref}</small></span>
        <span className="n">{vaults.length === 1 ? t("en una piscina") : t("en {n} piscinas", { n: vaults.length })}</span>
      </div>

      <div className="cifra">
        <span className="k">{t("Trabajando")}</span>
        <span className="v">{abiertas} <small>{t("de {n}", { n: vaults.length })}</small></span>
        <span className="n">{abiertas === vaults.length ? t("todas tienen posición abierta") : t("el resto está sin abrir")}</span>
      </div>

      <div className="cifra">
        <span className="k">{t("Cobrando ahora")}</span>
        <span className={`v ${enRango === abiertas && abiertas > 0 ? "sube" : ""}`}>
          {enRango} <small>{t("de {n}", { n: abiertas })}</small>
        </span>
        <span className="n">
          {abiertas === 0
            ? t("ninguna posición abierta todavía")
            : enRango === abiertas
              ? t("todas dentro de su rango")
              : t("las de fuera no cobran hasta que el robot las reajuste")}
        </span>
      </div>

      <div className="cifra">
        <span className="k">{t("Necesitan algo")}</span>
        {paradas.length === 0 ? (
          <>
            <span className="v estado bien">{t("Nada")}</span>
            <span className="n">{t("ninguna está parada ni a medio abrir")}</span>
          </>
        ) : (
          <>
            <span className="v estado ojo">{paradas.length === 1 ? t("{n} piscina", { n: paradas.length }) : t("{n} piscinas", { n: paradas.length })}</span>
            <span className="n">
              {paradas.some((v) => v.pausado) ? t("hay alguna en pausa") : t("hay dinero dentro sin posición abierta")}
            </span>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Las piscinas en forma de lista.
 *
 * ANTES ERAN TARJETAS EN REJILLA, y con seis o más el formato jugaba en contra: cada una repetía su
 * banda y su bloque de cifras, y comparar dos era ir y venir entre dos cuadros del mismo tamaño. En
 * filas, lo que se compara —capital, ganado, tiempo— cae en la misma columna y se lee de arriba
 * abajo sin buscarlo.
 */
function ListaPiscinas({ vaults, red, alAbrir }: {
  vaults: Vault[]; red: RedKeel; alAbrir: (d: Vault["direccion"]) => void;
}) {
  // Una sola consulta para todas: ver `useAperturas`.
  const aperturas = useAperturas(vaults.filter((v) => v.tokenId !== 0n).map((v) => v.direccion), red);
  // Y otra para lo ganado desde que abrieron. APARTE de la lista a proposito: son eventos, y en BNB
  // Chain eso son decenas de consultas troceadas — con las dos cosas juntas, la lista entera
  // esperaba a los eventos y la red parecia rota (ver `useCobrado`).
  const cobrado = useCobrado(vaults.map((v) => v.direccion), red);

  return (
    <ul className="lista-piscinas">
      {vaults.map((v) => (
        <FilaVault
          key={v.direccion}
          vault={v}
          red={red}
          alAbrir={alAbrir}
          apertura={aperturas.data?.[v.direccion.toLowerCase()]}
          cobrado={cobrado.data?.get(v.direccion.toLowerCase())}
        />
      ))}
    </ul>
  );
}


function FilaVault({ vault, red, alAbrir, apertura, cobrado }: {
  vault: Vault; red: RedKeel; alAbrir: (d: Vault["direccion"]) => void; apertura?: Apertura;
  cobrado?: Cobrado;
}) {
  const t = useT();
  const abierto = vault.tokenId !== 0n;
  const ref = vault.simboloRef ?? red.simboloEstable;
  const dec = vault.decimalesRef ?? 6;
  // CAPITAL HONESTO: el mayor entre el de estado en vivo y el de apertura. El vivo baja cuando el
  // dueño retira principal; usar el de apertura evita que el % se dispare (ver `ganado`/`useAperturas`).
  const capApertura = apertura?.capital ?? 0n;
  const capitalRaw = vault.costBasisRef > capApertura ? vault.costBasisRef : capApertura;
  const capital = Number(formatUnits(capitalRaw, dec));
  const reserva = Number(formatUnits(vault.reserva, dec));

  // DEPOSITADO SIN ABRIR: hay saldo dentro y ninguna posición. El `open()` no llegó a ejecutarse
  // (casi siempre por quedarse sin gas a mitad de la creación). El dinero está seguro dentro del
  // vault; solo falta un paso. Se distingue de «cerrada» —que está vacía— y se le da un CTA claro.
  const faltaAbrir = !abierto && ((vault.libre0 ?? 0n) > 0n || (vault.libre1 ?? 0n) > 0n);
  const dep0 = Number(formatUnits(vault.libre0 ?? 0n, vault.d0 ?? 18));
  const dep1 = Number(formatUnits(vault.libre1 ?? 0n, vault.d1 ?? 18));
  const deposito = dep0 > 0 ? { monto: dep0, sim: vault.s0 ?? ref } : { monto: dep1, sim: vault.s1 ?? ref };

  // EL PRECIO, AL DERECHO — igual que en la ficha. `precio` es token1 por token0 en crudo, y en un
  // par con el estable de token0 (USDG/NVDA) eso son "0,004428 NVDA por USDG": un número que no
  // significa nada para nadie. Invertido son 225,89 USDG por NVDA, que es como se dice el precio de
  // una acción. La ficha ya lo hacía y la lista no, así que las dos pantallas del mismo vault
  // enseñaban cifras distintas del mismo precio.
  //
  // Al invertir, los BORDES SE CRUZAN: el mínimo en la unidad de antes es el máximo en la nueva.
  // Pintarlos sin cambiarlos deja la banda del revés y el precio fuera de un rango en el que está.
  const vista = vault.s0 && vault.s1 && vault.precio != null
    ? vistaPrecio(vault.precio, vault.s0, vault.s1)
    : null;
  const bordes = vista && vault.lo != null && vault.hi != null
    ? (vista.invertido ? { lo: 1 / vault.hi, hi: 1 / vault.lo } : { lo: vault.lo, hi: vault.hi })
    : null;
  const enRango = bordes && vista ? vista.px >= bordes.lo && vista.px < bordes.hi : null;
  const dec2 = (x: number) => (x < 1 ? 6 : x < 100 ? 4 : 2);

  const g = ganado(vault, cobrado, capitalRaw);
  const tiempo = antiguedad(apertura?.abierta);
  const corta = `${vault.direccion.slice(0, 6)}…${vault.direccion.slice(-4)}`;

  return (
    <li
      className={`fila-piscina ${abierto ? "" : faltaAbrir ? "falta-abrir" : "cerrada"}`}
      onClick={() => alAbrir(vault.direccion)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && alAbrir(vault.direccion)}
    >
      {faltaAbrir && (
        <div className="cta-abrir" onClick={(e) => e.stopPropagation()}>
          <span className="txt">
            <strong>{t("Depositado, falta abrir la posición.")}</strong> {t("Tienes")}{" "}
            <b>{num(deposito.monto, 2)} {deposito.sim}</b> {t("dentro sin trabajar: la creación se quedó a medias (casi siempre por gas). Tu dinero está seguro — solo falta un paso.")}
          </span>
          <button type="button" onClick={() => alAbrir(vault.direccion)}>{t("Terminar de abrir →")}</button>
        </div>
      )}

      <div className="ident">
        {vault.s0 && vault.s1 && <ParMonedas s0={vault.s0} s1={vault.s1} size={26} />}
        <div className="nombre">
          <span className="par">{vault.par ?? "—"}</span>
          <span className="sub">
            {red.etiqueta}
            <code className="dir">{corta}</code>
            <Copiar texto={vault.direccion} etiqueta={t("la dirección")} />
          </span>
        </div>
      </div>

      {/* El precio, en la unidad en la que se dice: 222,63 USDG por NVDA, no 0,004492 NVDA por
          USDG. La banda cabe en una fila y sigue diciendo de un vistazo si está cobrando. */}
      <div className="precio">
        {abierto && vista && bordes ? (
          <>
            <span className="px">
              <strong>{num(vista.px, dec2(vista.px))}</strong> {vista.unidad}
              <i> {t("por {activo}", { activo: vista.activo })}</i>
            </span>
            <Banda precio={vista.px} lo={bordes.lo} hi={bordes.hi} alto={16} etiquetas={false} />
            <span className="rango-txt">
              {num(bordes.lo, dec2(bordes.lo))} – {num(bordes.hi, dec2(bordes.hi))}
            </span>
          </>
        ) : (
          <span className="rango-txt">{t("sin posición abierta")}</span>
        )}
      </div>

      <div className="cifra-fila">
        <span className="k">{t("Capital")}</span>
        <span className="v">{num(faltaAbrir ? deposito.monto : capital, 2)} <i>{faltaAbrir ? deposito.sim : ref}</i></span>
        {faltaAbrir ? <span className="n">{t("depositado, sin abrir")}</span> : reserva > 0 && <span className="n">{t("reserva {n}", { n: num(reserva, 2) })}</span>}
      </div>

      {/* GANADO Y % VAN JUNTOS: 0,12 USDC no dice nada sin saber sobre cuánto, y un +1,2% pelado
          tampoco dice cuánto dinero es. Separados obligan a hacer la cuenta mentalmente. */}
      <div className="cifra-fila">
        <span className="k">{t("Ganado")}</span>
        {g ? (
          <>
            <span className={`v ${g.importe > 0 ? "sube" : ""}`}>
              {g.importe > 0 ? "+" : ""}{num(g.importe, g.importe < 1 ? 4 : 2)} <i>{ref}</i>
            </span>
            <span className="n">
              {g.pct == null ? "—" : t("{pct}% sobre tu capital", { pct: `${g.pct > 0 ? "+" : ""}${num(g.pct, 2)}` })}
              {/* De dónde sale la cifra: lo que sigue dentro de la posición todavía no está
                  apartado, y saberlo evita la pregunta de por qué no cuadra con «Ganancias». */}
              {g.sinCobrar > 0 && (
                <> · {t("{n} sin barrer", { n: num(g.sinCobrar, g.sinCobrar < 1 ? 4 : 2) })}</>
              )}
            </span>
          </>
        ) : (
          <>
            <span className="v">—</span>
            <span className="n">{t("esta versión no lo publica")}</span>
          </>
        )}
      </div>

      <div className="cifra-fila">
        <span className="k">{t("Trabajando")}</span>
        <span className="v">{tiempo ?? "—"}</span>
        <span className="n">{abierto ? t("desde que se abrió") : faltaAbrir ? t("aún sin abrir") : t("cerrada")}</span>
      </div>

      <div className="estado-fila">
        {vault.pausado && <span className="pill aviso">{t("en pausa")}</span>}
        {!abierto && (faltaAbrir ? <span className="pill aviso">{t("falta abrir")}</span> : <span className="pill">{t("cerrada")}</span>)}
        {abierto && enRango != null && (
          <span className={`pill ${enRango ? "ok" : "aviso"}`}>
            {enRango ? t("cobrando") : t("fuera de rango")}
          </span>
        )}
        {vault.puedeActualizar && <span className="pill nuevo">{t("actualizable")}</span>}
        <span className="ir">→</span>
      </div>
    </li>
  );
}
